import { User } from '../types/user';
import { Project } from '../types/project';
import { Task } from '../types/task';
import { Organization } from '../types/organization';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import huTranslations from '../translations/hu';
import enTranslations from '../translations/en';

export interface NotificationRecipient {
  userId: string;
  email: string;
  name: string;
  language: 'hu' | 'en';
  role?: string;
}

export interface NotificationData {
  type: 'project' | 'task' | 'user';
  action: 'created' | 'updated' | 'status_changed' | 'assigned' | 'completed' | 'overdue' | 'due_soon' | 'invitation' | 'activated' | 'role_changed';
  recipients: NotificationRecipient[];
  data: any;
  organizationId: string;
  organizationName: string;
  triggeredBy: {
    userId: string;
    name: string;
    email: string;
  };
}

// Get translations for a specific language
const getTranslations = (language: 'hu' | 'en') => {
  return language === 'hu' ? huTranslations : enTranslations;
};

// Template helpers for handlebars-like syntax
const replaceTemplate = (template: string, data: Record<string, any>): string => {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return data[key] !== undefined ? String(data[key]) : match;
  });
};

// Format date for display
const formatDate = (date: Date | string | null, language: 'hu' | 'en'): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleDateString(language === 'hu' ? 'hu-HU' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Generate email content for different notification types
const generateEmailContent = (
  type: string,
  action: string,
  data: any,
  recipient: NotificationRecipient,
  organizationName: string,
  triggeredBy: any
) => {
  const translations = getTranslations(recipient.language);
  const notifications = translations.notifications;
  
  const commonData = {
    userName: recipient.name,
    organizationName,
    userEmail: recipient.email,
    userRole: notifications.roles[recipient.role as keyof typeof notifications.roles] || recipient.role,
    ...triggeredBy
  };

  let subject = '';
  let body = '';
  
  // Project notifications
  if (type === 'project') {
    const projectData = {
      ...commonData,
      projectName: data.name,
      projectDescription: data.description,
      startDate: formatDate(data.startDate, recipient.language),
      endDate: formatDate(data.endDate, recipient.language),
      priority: notifications.priorities[data.priority as keyof typeof notifications.priorities] || data.priority,
      createdBy: triggeredBy.name,
      updatedBy: triggeredBy.name,
      changedBy: triggeredBy.name,
      assignedBy: triggeredBy.name,
      completedBy: triggeredBy.name,
      completionDate: formatDate(data.completionDate, recipient.language),
      finalProgress: data.progress || 0,
      newStatus: notifications.statuses[data.newStatus as keyof typeof notifications.statuses] || data.newStatus,
      oldStatus: notifications.statuses[data.oldStatus as keyof typeof notifications.statuses] || data.oldStatus,
      changes: data.changes || ''
    };

    switch (action) {
      case 'created':
        subject = replaceTemplate(notifications.subjects.projectCreated, projectData);
        body = replaceTemplate(notifications.messages.projectCreated, projectData);
        break;
      case 'updated':
        subject = replaceTemplate(notifications.subjects.projectUpdated, projectData);
        body = replaceTemplate(notifications.messages.projectUpdated, projectData);
        break;
      case 'status_changed':
        subject = replaceTemplate(notifications.subjects.projectStatusChanged, projectData);
        body = replaceTemplate(notifications.messages.projectStatusChanged, projectData);
        break;
      case 'assigned':
        subject = replaceTemplate(notifications.subjects.projectAssigned, projectData);
        body = replaceTemplate(notifications.messages.projectAssigned, projectData);
        break;
      case 'completed':
        subject = replaceTemplate(notifications.subjects.projectCompleted, projectData);
        body = replaceTemplate(notifications.messages.projectCompleted, projectData);
        break;
    }
  }
  
  // Task notifications
  else if (type === 'task') {
    const taskData = {
      ...commonData,
      taskTitle: data.title,
      taskNumber: data.number || data.id,
      taskDescription: data.description,
      projectName: data.projectName,
      priority: notifications.priorities[data.priority as keyof typeof notifications.priorities] || data.priority,
      dueDate: formatDate(data.dueDate, recipient.language),
      createdBy: triggeredBy.name,
      updatedBy: triggeredBy.name,
      changedBy: triggeredBy.name,
      assignedBy: triggeredBy.name,
      completedBy: triggeredBy.name,
      completionDate: formatDate(data.completionDate, recipient.language),
      actualHours: data.actualHours,
      assignedTo: data.assignedToName,
      newStatus: notifications.statuses[data.newStatus as keyof typeof notifications.statuses] || data.newStatus,
      oldStatus: notifications.statuses[data.oldStatus as keyof typeof notifications.statuses] || data.oldStatus,
      changes: data.changes || '',
      daysOverdue: data.daysOverdue,
      daysUntilDue: data.daysUntilDue
    };

    switch (action) {
      case 'created':
        subject = replaceTemplate(notifications.subjects.taskCreated, taskData);
        body = replaceTemplate(notifications.messages.taskCreated, taskData);
        break;
      case 'updated':
        subject = replaceTemplate(notifications.subjects.taskUpdated, taskData);
        body = replaceTemplate(notifications.messages.taskUpdated, taskData);
        break;
      case 'assigned':
        subject = replaceTemplate(notifications.subjects.taskAssigned, taskData);
        body = replaceTemplate(notifications.messages.taskAssigned, taskData);
        break;
      case 'status_changed':
        subject = replaceTemplate(notifications.subjects.taskStatusChanged, taskData);
        body = replaceTemplate(notifications.messages.taskStatusChanged, taskData);
        break;
      case 'completed':
        subject = replaceTemplate(notifications.subjects.taskCompleted, taskData);
        body = replaceTemplate(notifications.messages.taskCompleted, taskData);
        break;
      case 'overdue':
        subject = replaceTemplate(notifications.subjects.taskOverdue, taskData);
        body = replaceTemplate(notifications.messages.taskOverdue, taskData);
        break;
      case 'due_soon':
        subject = replaceTemplate(notifications.subjects.taskDueSoon, taskData);
        body = replaceTemplate(notifications.messages.taskDueSoon, taskData);
        break;
    }
  }
  
  // User notifications
  else if (type === 'user') {
    const userData = {
      ...commonData,
      invitedBy: triggeredBy.name,
      oldRole: notifications.roles[data.oldRole as keyof typeof notifications.roles] || data.oldRole,
      newRole: notifications.roles[data.newRole as keyof typeof notifications.roles] || data.newRole,
      changedBy: triggeredBy.name
    };

    switch (action) {
      case 'invitation':
        subject = replaceTemplate(notifications.subjects.userInvitation, userData);
        body = replaceTemplate(notifications.messages.userInvitation, userData);
        break;
      case 'activated':
        subject = replaceTemplate(notifications.subjects.accountActivated, userData);
        body = replaceTemplate(notifications.messages.accountActivated, userData);
        break;
      case 'role_changed':
        subject = replaceTemplate(notifications.subjects.roleChanged, userData);
        body = replaceTemplate(notifications.messages.roleChanged, userData);
        break;
    }
  }

  // Add signature and footer
  const signature = replaceTemplate(notifications.common.signature, commonData);
  const footer = replaceTemplate(notifications.common.footer, commonData);
  
  const fullBody = `
    ${body}
    ${signature}
    ${footer}
    <p style="color: #999; font-size: 12px; margin-top: 20px;">
      ${notifications.common.doNotReply}
    </p>
  `;

  return {
    subject,
    body: fullBody
  };
};

// Send notification email
export const sendNotificationEmail = async (notificationData: NotificationData): Promise<void> => {
  const sendEmail = httpsCallable(functions, 'sendEmail');
  
  try {
    // Send email to each recipient in their preferred language
    const emailPromises = notificationData.recipients.map(async (recipient) => {
      const emailContent = generateEmailContent(
        notificationData.type,
        notificationData.action,
        notificationData.data,
        recipient,
        notificationData.organizationName,
        notificationData.triggeredBy
      );

      if (emailContent.subject && emailContent.body) {
        await sendEmail({
          to: recipient.email,
          subject: emailContent.subject,
          html: emailContent.body
        });
      }
    });

    await Promise.all(emailPromises);
    console.log('Notification emails sent successfully');
  } catch (error) {
    console.error('Error sending notification emails:', error);
    throw error;
  }
};

// Project notification helpers
export const notifyProjectCreated = async (
  project: Project,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'project',
    action: 'created',
    recipients,
    data: project,
    organizationId: project.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyProjectUpdated = async (
  project: Project,
  changes: string,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'project',
    action: 'updated',
    recipients,
    data: { ...project, changes },
    organizationId: project.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyProjectStatusChanged = async (
  project: Project,
  oldStatus: string,
  newStatus: string,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'project',
    action: 'status_changed',
    recipients,
    data: { ...project, oldStatus, newStatus },
    organizationId: project.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyProjectAssigned = async (
  project: Project,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'project',
    action: 'assigned',
    recipients,
    data: project,
    organizationId: project.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyProjectCompleted = async (
  project: Project,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'project',
    action: 'completed',
    recipients,
    data: { ...project, completionDate: new Date() },
    organizationId: project.organizationId,
    organizationName,
    triggeredBy
  });
};

// Task notification helpers
export const notifyTaskCreated = async (
  task: Task,
  projectName: string | null,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'task',
    action: 'created',
    recipients,
    data: { ...task, projectName },
    organizationId: task.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyTaskUpdated = async (
  task: Task,
  changes: string,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'task',
    action: 'updated',
    recipients,
    data: { ...task, changes },
    organizationId: task.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyTaskAssigned = async (
  task: Task,
  projectName: string | null,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'task',
    action: 'assigned',
    recipients,
    data: { ...task, projectName },
    organizationId: task.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyTaskStatusChanged = async (
  task: Task,
  oldStatus: string,
  newStatus: string,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'task',
    action: 'status_changed',
    recipients,
    data: { ...task, oldStatus, newStatus },
    organizationId: task.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyTaskCompleted = async (
  task: Task,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'task',
    action: 'completed',
    recipients,
    data: { ...task, completionDate: new Date() },
    organizationId: task.organizationId,
    organizationName,
    triggeredBy
  });
};

export const notifyTaskOverdue = async (
  task: Task,
  daysOverdue: number,
  recipients: NotificationRecipient[],
  organizationName: string
): Promise<void> => {
  await sendNotificationEmail({
    type: 'task',
    action: 'overdue',
    recipients,
    data: { ...task, daysOverdue },
    organizationId: task.organizationId,
    organizationName,
    triggeredBy: { userId: 'system', name: 'System', email: 'system@youcrm.hu' }
  });
};

export const notifyTaskDueSoon = async (
  task: Task,
  daysUntilDue: number,
  recipients: NotificationRecipient[],
  organizationName: string
): Promise<void> => {
  await sendNotificationEmail({
    type: 'task',
    action: 'due_soon',
    recipients,
    data: { ...task, daysUntilDue },
    organizationId: task.organizationId,
    organizationName,
    triggeredBy: { userId: 'system', name: 'System', email: 'system@youcrm.hu' }
  });
};

// User notification helpers
export const notifyUserInvitation = async (
  user: User,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'user',
    action: 'invitation',
    recipients,
    data: user,
    organizationId: user.organizationId || '',
    organizationName,
    triggeredBy
  });
};

export const notifyUserAccountActivated = async (
  user: User,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'user',
    action: 'activated',
    recipients,
    data: user,
    organizationId: user.organizationId || '',
    organizationName,
    triggeredBy
  });
};

export const notifyUserRoleChanged = async (
  user: User,
  oldRole: string,
  newRole: string,
  recipients: NotificationRecipient[],
  organizationName: string,
  triggeredBy: { userId: string; name: string; email: string }
): Promise<void> => {
  await sendNotificationEmail({
    type: 'user',
    action: 'role_changed',
    recipients,
    data: { ...user, oldRole, newRole },
    organizationId: user.organizationId || '',
    organizationName,
    triggeredBy
  });
}; 